/**
 * PDF2 Slide 3 - Base de Médicos
 * Endpoint: /api/v1/visitas/dashboards/base-medicos/
 * 
 * Filtros: ANO + MÊS em ambos os gráficos
 */
import { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { ResponsivePie } from "@nivo/pie";
import api from "../../../services/api";
import { MESES_COMPLETOS } from "./utils/mesesUtils";

const Slide7BaseMedicosOrigem = ({ programaId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ano = filters?.year || new Date().getFullYear();
  const mes = filters?.month;

  useEffect(() => {
    let url = `/visitas/dashboards/base-medicos/?programa_id=${programaId}&ano=${ano}`;
    if (mes !== null && mes !== undefined) {
      url += `&mes=${mes}`;
    }

    api.get(url)
      .then(res => {
        if (res.data) {
          setData(res.data);
        } else {
          setData({ status_base: [], origem_visitados: [] });
        }
      })
      .catch((err) => {
        console.error('[Slide7] API error:', err.response?.data || err.message);
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }, [programaId, ano, mes]);

  const labelAnoMes = mes ? `${MESES_COMPLETOS[mes]} ${ano}` : `Ano: ${ano}`;

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-4" />;

  if (error) {
    return (
      <Alert variant="warning" className="m-3">
        <strong>Seção 6 - Base de Médicos:</strong> {error}
      </Alert>
    );
  }

  const statusArr = Array.isArray(data?.status_base) ? data.status_base : [];
  const origemArr = Array.isArray(data?.origem_visitados) ? data.origem_visitados : [];
  if (data && (data.status_base !== undefined && !Array.isArray(data.status_base))) {
    console.warn('[Slide7] status_base não é array:', data.status_base);
  }
  if (data && (data.origem_visitados !== undefined && !Array.isArray(data.origem_visitados))) {
    console.warn('[Slide7] origem_visitados não é array:', data.origem_visitados);
  }
  const hasStatus = statusArr.length > 0;
  const hasOrigem = origemArr.length > 0;

  const statusPieData = hasStatus ? [
    {
      id: "Ativo",
      label: "Ativo",
      value: statusArr.find(s => s?.id === "Ativo")?.value || 0,
      color: "#ed7d31"
    },
    {
      id: "Inativo",
      label: "Inativo",
      value: statusArr.find(s => s?.id === "Inativo")?.value || 0,
      color: "#5b9bd5"
    }
  ] : [];

  const truncateLabel = (label, maxLength = 12) => {
    if (!label) return "";
    return label.length > maxLength ? label.substring(0, maxLength) + "..." : label;
  };
  const colorPalette = [
    "#5b9bd5", "#ed7d31", "#70ad47", "#ffc000", "#9e480e",
    "#7030a0", "#00b0f0", "#c00000", "#92d050", "#0070c0",
    "#ff6699", "#00b050", "#7f6000", "#4472c4", "#a5a5a5"
  ];

  const origemPieData = hasOrigem ? origemArr.map((item, index) => ({
    id: item?.id,
    label: truncateLabel(item?.label),
    fullLabel: item?.label,
    value: item?.value,
    color: colorPalette[index % colorPalette.length]
  })) : [];

  const pieConfig = {
    margin: { top: 30, right: 30, bottom: 80, left: 30 },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 3,
    enableArcLinkLabels: false,
    arcLabelsTextColor: "#fff",
    legends: [
      {
        anchor: "bottom",
        direction: "row",
        translateY: 50,
        itemWidth: 100,
        itemHeight: 20,
        symbolSize: 14,
        symbolShape: "circle"
      }
    ]
  };

  return (
    <Row>
      {/* Base Atual - Filtro: ANO + MÊS */}
      <Col md={6} className="mb-4">
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
            <span>Base Atual – Número de Médicos Totais</span>
            <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
          </Card.Header>
          <Card.Body style={{ height: "320px" }}>
            {hasStatus ? (
              <ResponsivePie
                data={statusPieData}
                colors={{ datum: "data.color" }}
                {...pieConfig}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <span className="text-muted">Sem registros disponíveis</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Origem dos Médicos Visitados - Filtro: ANO + MÊS */}
      <Col md={6} className="mb-4">
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
            <span>Origem dos Médicos Visitados</span>
            <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
          </Card.Header>
          <Card.Body style={{ height: "380px" }}>
            {hasOrigem ? (
              <ResponsivePie
                data={origemPieData}
                colors={{ datum: "data.color" }}
                margin={{ top: 20, right: 20, bottom: 120, left: 20 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                enableArcLinkLabels={false}
                arcLabelsTextColor="#fff"
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 80,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemsSpacing: 5,
                    symbolSize: 12,
                    symbolShape: "circle",
                    data: origemPieData.slice(0, 5).map(item => ({
                      id: item.id,
                      label: item.label,
                      color: item.color
                    }))
                  },
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 100,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemsSpacing: 5,
                    symbolSize: 12,
                    symbolShape: "circle",
                    data: origemPieData.slice(5, 10).map(item => ({
                      id: item.id,
                      label: item.label,
                      color: item.color
                    }))
                  }
                ]}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <span className="text-muted">Sem registros disponíveis</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default Slide7BaseMedicosOrigem;
